import React, { useMemo, useCallback, useEffect } from 'react';

import { useQuery } from '@apollo/client';
import { useVirtualizer } from '@tanstack/react-virtual';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import { LocationPicker } from '@adsk/acc-locations-components';
import { useAssigneePicker } from '@adsk/alloy-assignee-picker';
import { useCurrentProjectId } from '@adsk/alloy-provider';
import Badge from '@adsk/alloy-react-badge';
import { DateRangePicker } from '@adsk/alloy-react-date-picker';
import Dropdown, {
  useAutocomplete,
  VirtualizedMenu,
  AutocompleteControl,
  MultiDropdownTokenControl,
  MultiAutocompleteTokenControl,
} from '@adsk/alloy-react-dropdown';
import FormField from '@adsk/alloy-react-form-field';
import { MagnifyingGlassIcon, ICircleIcon } from '@adsk/alloy-react-icon';
import { useLocale } from '@adsk/alloy-react-locale';
import Panel from '@adsk/alloy-react-panel';
import TextInput from '@adsk/alloy-react-text-input';
import Theme from '@adsk/alloy-react-theme';
import Toggle from '@adsk/alloy-react-toggle';
import Tooltip from '@adsk/alloy-react-tooltip';

import { GET_TAGS, GET_SETTINGS } from './helpers/gql/queries';
import { FLAGS, isSettingActivated, SETTINGS } from './helpers/util';
import { usePlangridFeatureFlag } from './PlangridLaunchDarklyProvider';
import FiltersHeader from '../../FiltersHeader';

const AutotagsContainer = styled.div`
  display: flex;
  justify-content: flex-start;
`;

const PhotoFilter = ({
  onClose,
  open,
  filters,
  setFilters,
  clearFilters,
  hasFilters,
  floatingPanel,
}) => {
  const projectId = useCurrentProjectId();
  const { T } = useLocale(({ locale }) => require(`./i18n/${locale}.json`));
  const { value: canAutotag } = usePlangridFeatureFlag(
    FLAGS.PHOTO_AUTOTAGGING,
    false,
  );

  // check for hide-autotag setting
  const { data: settings } = useQuery(GET_SETTINGS, {
    variables: { projectId },
  });
  const items = settings?.ggPhotoSettingsByProject?.items;
  const hideAutotags =
    items && isSettingActivated(items, SETTINGS.HIDE_AUTOTAGS);

  const showAutotags = canAutotag && !hideAutotags;

  const typesToShow = useMemo(() => {
    if (showAutotags) return ['USER', 'ML'];
    return ['USER'];
  }, [showAutotags]);

  const { data } = useQuery(GET_TAGS, {
    variables: { projectId, types: typesToShow },
  });

  const imageTypeOptions = [
    { value: 'normal', label: T.translate('PHOTOS') },
    { value: 'video', label: T.translate('VIDEOS') },
    { value: 'photosphere', label: T.translate('360_PHOTOS') },
  ];

  const referencedByOptions = [
    { value: 'FORM', label: T.translate('FORM') },
    { value: 'ISSUE', label: T.translate('ISSUE') },
    { value: 'MARKUP', label: T.translate('SHEET') },
  ];

  const { value: canTag } = usePlangridFeatureFlag(FLAGS.PHOTO_TAGGING, false);
  const { value: canReference } = usePlangridFeatureFlag(
    FLAGS.REVERSE_REFERENCES,
    false,
  );

  const getLocalizedTag = useCallback(
    (tagName, tagType) => {
      if (tagType === 'ML') return T.translate(tagName);
      return tagName;
    },
    [T],
  );

  const tagOptions = useMemo(() => {
    const projectTags = data?.ggProjectByUid?.tags || [];
    return projectTags.map((t) => ({
      label: getLocalizedTag(t.name, t.type),
      value: t.name,
    }));
  }, [getLocalizedTag, data?.ggProjectByUid?.tags]);

  const { dropdownProps } = useAssigneePicker({
    userOptionValue: 'autodeskId',
    optionsOrder: ['user'],
    hideDeletedEntitiesInMenu: { users: false },
  });

  const users =
    dropdownProps.groupedItems?.find((g) => g.group === 'user')?.items || [];

  const updateFilters = (filtersToAdd) => {
    const newFilters = { ...filters };
    for (const [key, value] of Object.entries(filtersToAdd)) {
      if (!value || (Array.isArray(value) && value.length === 0)) {
        // Need to delete the entire key from the object because otherwise
        // ReferencePicker will understand it as if the filter exists
        // (even if the value is undefined or null)
        delete newFilters[key];
      } else {
        newFilters[key] = value;
      }
    }
    setFilters(newFilters);
  };

  const { controlProps, menuProps, virtualProps, setInputValue } =
    useAutocomplete({
      items: users,
      initialIsOpen: false,
      selectedItem:
        users.find((o) => filters?.creatorIds?.includes(o.value)) || null,
      onSelectedItemChange: ({ selectedItem }) =>
        updateFilters({ creatorIds: selectedItem?.value }),
    });
  const { getTotalSize, getVirtualItems } = useVirtualizer(virtualProps);

  // The useEffect is need for the clear the uploaded by dropdown from outer component
  // Once useAutoComplete hook version updated single auto complete option we'll revert this changes
  useEffect(() => {
    if (!filters?.creatorIds) {
      setInputValue(null);
    }
  }, [filters?.creatorIds, setInputValue]);

  return (
    <Panel
      open={open}
      floating={floatingPanel}
      renderHeader={() => (
        <FiltersHeader
          floatingPanel={floatingPanel}
          onClose={onClose}
          title={T.translate('FILTER_TITLE')}
          hasFilters={hasFilters}
          clearFilters={() => {
            clearFilters();
            setInputValue(null);
          }}
        />
      )}
    >
      <Panel.Body>
        <div style={{ marginBottom: 16 }}>
          <TextInput
            style={{
              flex: 1,
            }}
            placeholder={T.translate('SEARCH_TITLE')}
            renderRightElement={(p) => <MagnifyingGlassIcon {...p} />}
            value={filters.title || ''}
            onChange={(e) => updateFilters({ title: e.target.value })}
          />
        </div>
        <FormField
          label={T.translate('LOCATION')}
          labelVariant={FormField.LABEL_VARIANTS.TOP}
          style={{ marginBottom: 16 }}
        >
          <LocationPicker
            clearable
            multiple
            maxCardHeight="384px"
            selectedNodesIds={filters?.locationIds ?? []}
            onNodeSelect={(locations) => {
              const locationIds = Array.isArray(locations)
                ? locations.map((l) => l.id)
                : locations?.id;
              updateFilters({ locationIds });
            }}
            includeSubLocations={filters.includeSubLocations}
            onIncludeSubLocation={(include) =>
              updateFilters({ includeSubLocations: include })
            }
          />
        </FormField>

        <FormField
          label={T.translate('DATE_ADDED')}
          labelVariant={FormField.LABEL_VARIANTS.TOP}
          style={{ marginBottom: 16 }}
        >
          <DateRangePicker
            startDate={
              filters?.createdAfter ? new Date(filters.createdAfter) : null
            }
            endDate={
              filters?.createdBefore ? new Date(filters.createdBefore) : null
            }
            onChange={({ startDate, endDate }) =>
              updateFilters({
                createdAfter: startDate?.valueOf(),
                createdBefore: endDate?.valueOf(),
              })
            }
          />
        </FormField>
        <FormField
          label={T.translate('DATE_TAKEN')}
          labelVariant={FormField.LABEL_VARIANTS.TOP}
          style={{ marginBottom: 16 }}
        >
          <DateRangePicker
            startDate={
              filters.afterTakenOn ? new Date(filters.afterTakenOn) : null
            }
            endDate={
              filters.beforeTakenOn ? new Date(filters.beforeTakenOn) : null
            }
            onChange={({ startDate, endDate }) =>
              updateFilters({
                afterTakenOn: startDate?.valueOf(),
                beforeTakenOn: endDate?.valueOf(),
              })
            }
          />
        </FormField>
        <FormField
          label={T.translate('PHOTO_TYPE')}
          labelVariant={FormField.LABEL_VARIANTS.TOP}
          style={{ marginBottom: 16 }}
        >
          <Dropdown
            style={{ width: '100%' }}
            isMultiple
            items={imageTypeOptions}
            selectedItems={imageTypeOptions?.filter((o) =>
              filters?.imageTypes?.includes(o.value),
            )}
            onSelectedItemsChange={({ selectedItems }) =>
              updateFilters({ imageTypes: selectedItems?.map((i) => i.value) })
            }
            renderControl={(args) => <MultiDropdownTokenControl {...args} />}
          />
        </FormField>
        {canReference && (
          <FormField
            label={T.translate('REFERENCED_BY')}
            labelVariant={FormField.LABEL_VARIANTS.TOP}
            style={{ marginBottom: 8 }}
          >
            <Dropdown
              style={{ width: '100%' }}
              isMultiple
              items={referencedByOptions}
              selectedItems={referencedByOptions?.filter((o) =>
                filters?.referencedBy?.includes(o.value),
              )}
              onSelectedItemsChange={({ selectedItems }) =>
                updateFilters({
                  referencedBy: selectedItems?.map((r) => r.value),
                })
              }
              renderControl={(args) => <MultiDropdownTokenControl {...args} />}
            />
          </FormField>
        )}
        {canTag && (
          <FormField
            label={T.translate('TAGS')}
            labelVariant={FormField.LABEL_VARIANTS.TOP}
            style={{ marginBottom: 8 }}
          >
            <Dropdown
              style={{ width: '100%' }}
              isMultiple
              isAutocomplete
              items={tagOptions}
              selectedItems={tagOptions?.filter((o) =>
                filters?.tags?.includes(o.value),
              )}
              onSelectedItemsChange={({ selectedItems }) =>
                updateFilters({ tags: selectedItems?.map((t) => t.value) })
              }
              renderControl={(args) => (
                <MultiAutocompleteTokenControl {...args} />
              )}
            />
          </FormField>
        )}

        {showAutotags && (
          <AutotagsContainer>
            <span>
              {T.translate('SHOW_AUTOMATICALLY_TAGGED_RESULTS')}
              <div>
                <Tooltip
                  trigger="click"
                  content={
                    <span>
                      {T.translate('SHOW_AUTOMATICALLY_TAGGED_RESULTS_TOOLTIP')}

                      <a
                        href="https://help.autodesk.com/view/BUILD/ENU/?guid=Photos_Autotag_TC"
                        target="_blank"
                        rel="noreferrer"
                      >
                        {T.translate('TERMS_AND_CONDITIONS')}
                      </a>
                    </span>
                  }
                >
                  <ICircleIcon size={14} />
                </Tooltip>
              </div>
            </span>

            <a
              href="https://help.autodesk.com/view/BUILD/ENU/?guid=Photos_Autotag_TC"
              target="_blank"
              rel="noreferrer"
            >
              <Badge
                color={Theme.colors.yellowOrange300}
                size="medium"
                textColor={Theme.colors.charcoal900}
              >
                {T.translate('BETA')}
              </Badge>
            </a>
            <Toggle
              checked={!filters.excludeAutotags}
              onChange={() =>
                updateFilters({
                  excludeAutotags: filters?.excludeAutotags ? null : true,
                })
              }
            />
          </AutotagsContainer>
        )}
        <FormField
          label={T.translate('UPLOADED_BY')}
          labelVariant={FormField.LABEL_VARIANTS.TOP}
        >
          <>
            <AutocompleteControl {...controlProps} />
            <VirtualizedMenu
              {...menuProps}
              setScrollElement={virtualProps.setScrollElement}
              totalSize={getTotalSize()}
              virtualItems={getVirtualItems()}
            />
          </>
        </FormField>
      </Panel.Body>
    </Panel>
  );
};

PhotoFilter.propTypes = {
  onClose: PropTypes.func,
  open: PropTypes.bool,
  filters: PropTypes.shape({
    title: PropTypes.string,
    createdAfter: PropTypes.number,
    createdBefore: PropTypes.number,
    afterTakenOn: PropTypes.number,
    beforeTakenOn: PropTypes.number,
    creatorIds: PropTypes.arrayOf(PropTypes.string),
    imageTypes: PropTypes.arrayOf(PropTypes.string),
    referencedBy: PropTypes.arrayOf(PropTypes.string),
    tags: PropTypes.arrayOf(PropTypes.string),
    excludeAutotags: PropTypes.bool,
    locationIds: PropTypes.arrayOf(PropTypes.string),
    includeSubLocations: PropTypes.bool,
  }),
  setFilters: PropTypes.func,
  clearFilters: PropTypes.func,
  hasFilters: PropTypes.bool,
  floatingPanel: PropTypes.bool,
};

export default PhotoFilter;
